import React, { useMemo } from 'react';
import { Button } from '../components/common';
import { Heading, Typography } from '../components/common/Typography';
import { SITE_USER_ROLES } from '../utils/roleUtils';
import { HOMEPAGE_MODE } from '../utils/homePageMode';
import { useHomeDashboardData } from '../hooks/useHomeDashboardData';
import {
  HomeSessionsCarousel,
  HomeQuickActions,
  HomeYourClubs,
  HomeActivityFeed,
  HomeFrequentMatchups,
  HomeLastSessionHero,
  HomeQuickActionsSkeleton,
  HomeSessionsCarouselSkeleton,
  HomeYourClubsSkeleton,
  HomeMomentsSkeleton,
  HomeMatchupsSkeleton,
  HomeLastSessionHeroSkeleton,
} from '../components/home';
import { isMatchupEvent } from '../services/activityFeedService';
import GuestAccountMergesSection from '../components/guest/GuestAccountMergesSection';
import {
  homePageRoot,
  homeDashboardPageRoot,
  homeDashboardColumn,
  homeMainStack,
  homeElevatedInset,
} from '../components/home/homeDashboardStyles';

const Home = ({ currentUser }) => {
  const {
    error,
    membershipsPending,
    sessionDeckPending,
    clubsPending,
    momentsFeedPending,
    momentsFeedError,
    matchupsPending,
    lastSessionPending,
    sessionDeckError,
    clubsError,
    nextGame,
    upcomingInTwoWeeks,
    homepageMode,
    clubsSnapshot,
    momentsFeedItems,
    matchupsItems,
    lastSessionSnapshot,
    quickActionsFlags,
    performanceClubs,
    appClubsTotal,
    memberClubIds,
    refresh,
  } = useHomeDashboardData(currentUser);

  const isSuperAdmin = currentUser?.role === SITE_USER_ROLES.SUPER_ADMIN;

  const quickActionsHint = useMemo(() => {
    if (homepageMode === HOMEPAGE_MODE.ONBOARDING) {
      return 'Start here: browse open games or join a club so we can personalise this dashboard.';
    }
    return undefined;
  }, [homepageMode]);

  // Performance events go in the Moments strip; matchup events go in the Matchups section
  const performanceFeedItems = useMemo(
    () => (momentsFeedItems || []).filter(e => !isMatchupEvent(e.event_type)),
    [momentsFeedItems]
  );

  const matchupFeedItems = useMemo(
    () => (momentsFeedItems || []).filter(e => isMatchupEvent(e.event_type)),
    [momentsFeedItems]
  );

  const showMomentsFeed =
    momentsFeedPending || momentsFeedError || performanceFeedItems.length > 0;

  const showQuickActionsEarly =
    homepageMode === HOMEPAGE_MODE.ONBOARDING ||
    homepageMode === HOMEPAGE_MODE.DISCOVERY;

  const quickActionsSection = membershipsPending ? (
    <HomeQuickActionsSkeleton />
  ) : (
    <HomeQuickActions
      canCreateSession={quickActionsFlags.canCreateSession}
      clubsForSessionCreate={quickActionsFlags.clubsForSessionCreate}
      performanceClubs={performanceClubs}
      isSuperAdmin={isSuperAdmin}
      homepageMode={homepageMode}
      hint={quickActionsHint}
      appClubsTotal={appClubsTotal}
      className={showQuickActionsEarly ? 'mb-8 sm:mb-10' : 'mt-8'}
    />
  );

  if (error) {
    return (
      <div className={homePageRoot}>
        <div className="max-w-app-shell mx-auto py-8 px-4">
          <div className="text-center">
            <Typography variant="h4" className="mb-4 text-error">Error loading dashboard</Typography>
            <Typography variant="muted" className="mb-6">{error}</Typography>
            <Button onClick={() => refresh()} variant="primary">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={homeDashboardPageRoot}>
      <div className={`${homeDashboardColumn} pb-16 pt-5 sm:pt-6`}>

        {lastSessionPending ? (
          <HomeLastSessionHeroSkeleton />
        ) : (
          <HomeLastSessionHero
            currentUser={currentUser}
            snapshot={lastSessionSnapshot}
            loading={false}
          />
        )}

        <GuestAccountMergesSection
          currentUser={currentUser}
          homeDashboardSection
          onGuestsResolved={refresh}
        />

        {showQuickActionsEarly ? quickActionsSection : null}

        <main className={homeMainStack}>
        {sessionDeckError ? (
          <div className={`${homeElevatedInset} px-4 py-3 flex flex-wrap items-center justify-between gap-3`}>
            <Typography variant="body-small" className="text-error">
              {sessionDeckError}
            </Typography>
            <Button type="button" variant="outline" size="small" onClick={() => refresh()}>
              Retry
            </Button>
          </div>
        ) : null}

        {sessionDeckPending ? (
          <HomeSessionsCarouselSkeleton />
        ) : (
          <HomeSessionsCarousel
            nextSession={nextGame}
            upcomingSessions={upcomingInTwoWeeks}
            currentUser={currentUser}
            memberClubIds={memberClubIds}
            onSessionsChanged={refresh}
          />
        )}

        {clubsPending ? (
          <HomeYourClubsSkeleton />
        ) : clubsError ? (
          <div className="mb-10">
            <Heading level={2} className="mb-5 text-lg sm:text-xl font-bold tracking-tight text-text-primary">Your clubs</Heading>
            <div className={`${homeElevatedInset} px-4 py-3 flex flex-wrap items-center justify-between gap-3`}>
              <Typography variant="body-small" className="text-error">
                {clubsError}
              </Typography>
              <Button type="button" variant="outline" size="small" onClick={() => refresh()}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <HomeYourClubs clubs={clubsSnapshot} loading={false} />
        )}

        {showMomentsFeed ? (
          momentsFeedPending ? (
            <HomeMomentsSkeleton />
          ) : momentsFeedError ? (
            <div className="mb-10">
              <Heading level={2} className="mb-5 text-lg sm:text-xl font-bold tracking-tight text-text-primary">Your Moments</Heading>
              <div className={`${homeElevatedInset} px-4 py-3 flex flex-wrap items-center justify-between gap-3`}>
                <Typography variant="body-small" className="text-error">
                  {momentsFeedError}
                </Typography>
                <Button type="button" variant="outline" size="small" onClick={() => refresh()}>
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <HomeActivityFeed
              items={performanceFeedItems}
              currentUser={currentUser}
              loading={false}
            />
          )
        ) : null}

        {matchupsPending ? (
          <HomeMatchupsSkeleton />
        ) : (matchupsItems && matchupsItems.length > 0) || matchupFeedItems.length > 0 ? (
          <HomeFrequentMatchups
            matchups={matchupsItems}
            recentEvents={matchupFeedItems}
            loading={false}
          />
        ) : null}

        {!showQuickActionsEarly ? quickActionsSection : null}
        </main>
      </div>
    </div>
  );
};

export default Home;
